import React, { useEffect, useState } from 'react';
import { Users, Repeat, MousePointerClick, Activity } from 'lucide-react';
import crm from '../crm/api';

const Card = ({ children, className = '' }) => (
  <div className={`bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-5 ${className}`}>{children}</div>
);

// horizontal bar list
function Bars({ data, color = 'var(--acc)', empty = 'No data yet' }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  if (!data.length) return <div className="text-xs text-[var(--muted)] py-4">{empty}</div>;
  return (
    <div className="space-y-2.5">
      {data.map((d) => (
        <div key={d.key} className="flex items-center gap-3">
          <div className="w-36 shrink-0 text-[13px] truncate text-[var(--text)]" title={d.key}>{d.key}</div>
          <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--soft)' }}>
            <div className="h-full rounded-full" style={{ width: `${(d.count / max) * 100}%`, background: color }} />
          </div>
          <div className="w-8 text-right text-[13px] font-bold text-[var(--text)] tabular-nums">{d.count}</div>
        </div>
      ))}
    </div>
  );
}

export default function Visitors() {
  const [d, setD] = useState(null);
  useEffect(() => { crm.get('/visitors/analytics').then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  if (!d) return <div className="pt-10 text-[var(--muted)]">Loading…</div>;

  const kpis = [
    { label: 'Visitors', value: d.totalVisitors || 0, icon: Users, from: '#10b981', to: '#0e7490' },
    { label: 'Returning', value: d.returning || 0, icon: Repeat, from: '#0ea5e9', to: '#2563eb' },
    { label: 'Engagements', value: d.events || 0, icon: MousePointerClick, from: '#7c3aed', to: '#6366f1' },
    { label: 'Conversion', value: `${d.conversion || 0}%`, icon: Activity, from: '#f97316', to: '#dc2626' },
  ];
  const funnelMax = Math.max(1, ...(d.funnel || []).map((f) => f.count));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-[var(--text)] tracking-tight">Visitor Analytics</h1>
        <p className="text-sm text-[var(--muted)]">Every card & microsite touch — where interest turns into leads.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const I = k.icon;
          return (
            <div key={k.label} className="rounded-3xl p-5 text-white card-lux" style={{ background: `linear-gradient(135deg, ${k.from}, ${k.to})` }}>
              <div className="flex items-center justify-between">
                <I size={22} className="opacity-90" />
              </div>
              <div className="text-3xl font-extrabold mt-3 leading-none">{k.value}</div>
              <div className="text-[13px] font-semibold opacity-90 mt-1">{k.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Engagement funnel */}
        <Card className="lg:col-span-3">
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">Engagement funnel</h3>
          <div className="space-y-2.5">
            {(d.funnel || []).map((f, i) => (
              <div key={f.key} className="flex items-center gap-3">
                <div className="w-40 shrink-0 text-[13px] text-[var(--text)] font-medium">{f.key}</div>
                <div className="flex-1 h-7 rounded-lg overflow-hidden relative" style={{ background: 'var(--soft)' }}>
                  <div className="h-full rounded-lg flex items-center px-2 text-white text-[12px] font-bold transition-all"
                    style={{ width: `${Math.max(6, (f.count / funnelMax) * 100)}%`, background: `linear-gradient(90deg, hsl(${160 - i * 22} 65% 42%), hsl(${160 - i * 22} 70% 34%))` }}>
                    {f.count}
                  </div>
                </div>
                <div className="w-10 text-right text-[12px] text-[var(--muted)] tabular-nums">{d.totalVisitors ? Math.round((f.count / d.totalVisitors) * 100) : 0}%</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top products */}
        <Card className="lg:col-span-2">
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">Most-viewed products</h3>
          <Bars data={d.topProducts || []} color="var(--acc)" empty="No product views yet" />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card>
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">By source</h3>
          <Bars data={d.bySource || []} color="#2563eb" />
        </Card>
        <Card>
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">By device</h3>
          <Bars data={d.byDevice || []} color="#7c3aed" />
        </Card>
        <Card>
          <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-4">Traffic by rep</h3>
          <Bars data={d.byEmployee || []} color="#0b6e3e" empty="Employee view only" />
        </Card>
      </div>

      {/* Recent visitors */}
      <Card className="p-0 overflow-hidden">
        <h3 className="font-extrabold text-[var(--text)] text-[16px] px-5 pt-5 pb-3">Recent visitors</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-[var(--soft)] text-[var(--muted)] text-xs uppercase">
              <tr>{['Device', 'Browser', 'Visits', 'Source', 'Last seen'].map((h) => <th key={h} className="text-left px-5 py-2.5 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(d.recent || []).length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[var(--muted)]">No visitors yet.</td></tr>}
              {(d.recent || []).map((v, i) => (
                <tr key={i} className="border-t border-[var(--border)]">
                  <td className="px-5 py-2.5 text-[var(--text)] capitalize">{v.device || 'desktop'}</td>
                  <td className="px-5 py-2.5 text-[var(--muted)]">{v.browser} · {v.os}</td>
                  <td className="px-5 py-2.5 text-[var(--text)] font-semibold tabular-nums">{v.visitCount}</td>
                  <td className="px-5 py-2.5"><span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: 'var(--soft)', color: 'var(--muted)' }}>{v.source || '—'}</span></td>
                  <td className="px-5 py-2.5 text-[var(--muted)] text-[13px]">{v.lastSeen ? new Date(v.lastSeen).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
